import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, TrendingUp, Server, Users, Clock, Check, ArrowRight, Landmark, CreditCard } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const features = [
  {
    icon: Lock,
    title: "Strong encryption",
    description: "WireGuard's ChaCha20 encryption protects financial transactions, client portfolios, and sensitive market data in transit."
  },
  {
    icon: TrendingUp,
    title: "Light footprint",
    description: "WireGuard's lean design keeps overhead low for trading platforms, market data feeds, and financial applications."
  },
  {
    icon: Server,
    title: "Secure branch connectivity",
    description: "Connect bank branches, trading floors, and remote offices with encrypted site-to-site VPN tunnels."
  },
  {
    icon: Shield,
    title: "Compliance support",
    description: "Support the network-security controls behind SEC, FINRA, SOX, and PCI-DSS programs with encrypted connections and device records."
  },
  {
    icon: Users,
    title: "Role-based access",
    description: "Granular permissions for traders, analysts, compliance officers, and support staff with centralized management."
  },
  {
    icon: CreditCard,
    title: "Cardholder data in transit",
    description: "Encrypt connections to cardholder data environments to support your PCI-DSS transmission-security controls."
  }
];

const useCases = [
  "Remote trading and portfolio management",
  "Secure access to banking core systems",
  "Client financial data protection",
  "Multi-branch bank connectivity",
  "Remote work for financial advisors",
  "Secure wire transfer authorization",
  "Compliance audit trail access",
  "Mobile banking app security"
];

const complianceStandards = [
  { title: "SOX", desc: "Financial reporting security" },
  { title: "PCI-DSS", desc: "Cardholder data protection" },
  { title: "SEC/FINRA", desc: "Securities industry programs" },
  { title: "GLBA", desc: "Customer information safeguards" }
];

export default function FinanceVPN() {
  return (
    <>
      <Helmet>
        <title>VPN for Financial Services | Banking & Trading VPN Security | SACVPN</title>
        <meta
          name="description"
          content="Secure VPN for banks, trading firms, and financial services. WireGuard encryption for data in transit and compliance support for financial institutions."
        />
        <meta
          name="keywords"
          content="financial VPN, banking VPN, trading VPN, financial services VPN, PCI DSS VPN, SOX VPN, bank security, financial institution cybersecurity"
        />
        <link rel="canonical" href="https://www.sacvpn.com/industries/finance" />
        <meta property="og:title" content="VPN for Financial Services | Banking & Trading VPN Security | SACVPN" />
        <meta property="og:description" content="Secure VPN for banks, trading firms, and financial services. WireGuard encryption for data in transit and compliance support for financial institutions." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Financial Services VPN",
            "name": "SACVPN Financial Services VPN Solutions",
            "description": "Secure VPN service for banks, trading firms, and financial institutions with regulatory compliance support.",
            "provider": {
              "@type": "Organization",
              "name": "SACVPN"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Financial Services Organizations"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_78%_20%,rgba(14,165,233,0.14),transparent_60%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Financial services
              </motion.p>

              <motion.h1
                variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight"
              >
                Secure VPN for{" "}
                <span className="text-brand-400">finance</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-lg text-gray-400 leading-relaxed max-w-xl"
              >
                Protect financial transactions and client data with strong encryption. WireGuard connections for banks, trading firms, and financial institutions.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
                >
                  <Clock className="w-5 h-5" />
                  Start free trial
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
                >
                  Request demo
                </Link>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="relative">
              <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Financial security</h3>
                    <p className="text-gray-500 text-sm">Encryption for data in transit</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {["WireGuard (ChaCha20) encryption", "Supports PCI-DSS controls", "Device provisioning records", "Authenticated device access"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-gray-400">
                      <Check className="w-4 h-4 text-brand-400 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Regulations</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Compliance support
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              Encrypted connections and device records help you address the network-security controls behind these programs. Full compliance is a program you run across your organization.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {complianceStandards.map((point, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                  <Check className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{point.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Capabilities</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Built for financial security
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden"
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="bg-dark-900 p-8 hover:bg-dark-800 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Use cases
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-6"
              >
                Financial services use cases
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="text-lg text-gray-400 mb-8 leading-relaxed"
              >
                From trading floors to remote advisors, SACVPN secures the connections across your financial operations.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                {useCases.map((useCase, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{useCase}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8"
            >
              <h3 className="text-xl font-semibold text-white mb-6">Why financial teams choose SACVPN</h3>
              <div className="space-y-4">
                {[
                  "Deploy globally in minutes",
                  "Light footprint on every connection",
                  "Scales from small firms to enterprises",
                  "Dedicated account management",
                  "Device provisioning records for audits"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-400">
                    <ArrowRight className="w-5 h-5 text-brand-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Secure your financial operations
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Protect transactions and client data with WireGuard encryption. Start your 14-day free trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Contact sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
